import { Epic, ofType } from "redux-observable";
import { catchError, map, merge, mergeMap, of, takeUntil } from "rxjs";
import { IAction } from "../../../interface/action.interface";
import { IErrorDto } from "../../../interface/dto/error.dto";
import { IRootState } from "../../../redux/root.reducer";
import {
    deleteAuthTokens,
    selectAuthTokens,
    setAuthTokens,
} from "../selectors/auth.selector";
import { login } from "../service/auth.api";
import {
    getIntendedReturnTo,
    getLoginRedirectAddress,
    getRequestAccessRedirectAddress,
} from "../service/redirect";
import { getUserDetails } from "../service/user-details.api";
import {
    AuthActionType,
    IAuthInitAction,
    IAuthLoginAction,
    IAuthLoginErrorAction,
    IAuthLoginSuccessAction,
    IAuthRedirectAction,
    IAuthRequestAccessAction,
    IUserDetailsGetAction,
    authLoginError,
    authLoginSuccess,
    userDetailsGetAction,
    userDetailsGetErrorAction,
    userDetailsGetSuccessAction,
} from "./auth.actions";

export const authEpic: Epic<IAction, IAction, IRootState> = (action$) =>
    merge(
        action$.pipe(
            ofType<IAuthInitAction, AuthActionType.AUTH_INIT>(
                AuthActionType.AUTH_INIT,
            ),
            mergeMap(() => {
                const { accessToken, refreshToken } = selectAuthTokens();
                return [
                    ...(accessToken && refreshToken
                        ? [userDetailsGetAction()]
                        : []),
                ];
            }),
        ),
        action$.pipe(
            ofType<IAuthRedirectAction, AuthActionType.AUTH_REDIRECT>(
                AuthActionType.AUTH_REDIRECT,
            ),
            mergeMap(() => {
                window.location.href = getLoginRedirectAddress();
                return of();
            }),
        ),
        action$.pipe(
            ofType<IAuthLoginAction, AuthActionType.AUTH_LOGIN>(
                AuthActionType.AUTH_LOGIN,
            ),
            mergeMap((action) => {
                deleteAuthTokens();
                return login(action.payload.code).pipe(
                    takeUntil(
                        action$.pipe(
                            ofType<IAuthLoginAction, AuthActionType.AUTH_LOGIN>(
                                AuthActionType.AUTH_LOGIN,
                            ),
                        ),
                    ),
                    map(({ accessToken, refreshToken }) => {
                        setAuthTokens(accessToken, refreshToken);
                        return authLoginSuccess(accessToken, refreshToken);
                    }),
                    catchError((error: string) => of(authLoginError(error))),
                );
            }),
        ),
        action$.pipe(
            ofType<IAuthLoginSuccessAction, AuthActionType.AUTH_LOGIN_SUCCESS>(
                AuthActionType.AUTH_LOGIN_SUCCESS,
            ),
            mergeMap(() => {
                window.location.replace(getIntendedReturnTo() ?? "/");
                return of();
            }),
        ),
        action$.pipe(
            ofType<IAuthLoginErrorAction, AuthActionType.AUTH_LOGIN_ERROR>(
                AuthActionType.AUTH_LOGIN_ERROR,
            ),
            mergeMap(() => {
                window.location.href = getRequestAccessRedirectAddress();
                return of();
            }),
        ),
        action$.pipe(
            ofType<
                IAuthRequestAccessAction,
                AuthActionType.AUTH_REQUEST_ACCESS
            >(AuthActionType.AUTH_REQUEST_ACCESS),
            mergeMap(() => {
                window.location.href = getRequestAccessRedirectAddress();
                return of();
            }),
        ),
        action$.pipe(
            ofType<IUserDetailsGetAction, AuthActionType.USER_DETAILS_GET>(
                AuthActionType.USER_DETAILS_GET,
            ),
            mergeMap(() =>
                getUserDetails(selectAuthTokens()).pipe(
                    takeUntil(
                        action$.pipe(
                            ofType<
                                IUserDetailsGetAction,
                                AuthActionType.USER_DETAILS_GET
                            >(AuthActionType.USER_DETAILS_GET),
                        ),
                    ),
                    map((result) =>
                        result
                            ? userDetailsGetSuccessAction(result)
                            : userDetailsGetErrorAction(
                                  "Error while fetching!!",
                              ),
                    ),
                    catchError((error: IErrorDto) =>
                        of(userDetailsGetErrorAction(error.message)),
                    ),
                ),
            ),
        ),
    );
